import React, { useState } from "react";
import { 
  BookOpen, Save, Mail, Download, Printer, ShieldCheck, Clipboard, FileText, ChevronDown, ChevronUp, Sparkles, Send 
} from "lucide-react";

export default function JBLPortalGuide() {
  const [openSection, setOpenSection] = useState("certification");

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-amber-600 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl mb-8 relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-6 -translate-y-6 opacity-10">
          <BookOpen className="w-64 h-64" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-xs font-semibold uppercase tracking-wider text-amber-200 mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Staff Training Reference
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">JBL Portal User Guide</h1>
            <p className="mt-2 text-slate-300 text-sm md:text-base leading-relaxed">
              Learn how to manage drafts, save completed forms, and email submissions for all Jarrod Behan Law workflow tools.
            </p>
          </div>
        </div>
      </div>

      {/* Accordions */}
      <div className="space-y-4">
        
        {/* 1. JBL Guideline Certification */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("certification")}
            className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">1. JBL Guideline Certification</h3>
                <p className="text-xs text-slate-400 mt-0.5">VLA compliance check & demographics syncing</p>
              </div>
            </div>
            {openSection === "certification" ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {openSection === "certification" && (
            <div className="px-6 pb-6 pt-2 border-t border-slate-100 space-y-6 text-sm text-slate-650 leading-relaxed">
              <div>
                <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> What it does</h4>
                <p>Verifies whether a client’s charges and financial situations comply with Victoria Legal Aid guidelines. Certifies grant eligibility before submitting applications.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Save className="w-4 h-4 text-amber-600" /> Saving Drafts</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Autosave</strong>: Saves your progress in your browser as you type.</li>
                    <li><strong>Draft Share</strong>: Click <em>"Share Draft Link"</em> to copy a direct URL containing all current inputs.</li>
                    <li><strong>Export/Import</strong>: Download a backup `.json` file to your PC and upload it later to resume.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Printer className="w-4 h-4 text-amber-600" /> Final Outputs</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Generate Certificate</strong>: Syncs demographics (Name, DOB, File Ref) to browser storage so other tools pre-fill automatically.</li>
                    <li><strong>Print/PDF</strong>: Opens browser Print dialog to send to a physical printer or save to PDF.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Send className="w-4 h-4 text-amber-600" /> Emailing</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Inbox Submission</strong>: Finalizing the certificate automatically delivers details directly to the central <strong>Submissions Inbox</strong>.</li>
                    <li><strong>Share Draft</strong>: Directly email draft links to team members using the share modal.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. JBL Legal Aid Application */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("aid-form")}
            className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">2. JBL Legal Aid Application</h3>
                <p className="text-xs text-slate-400 mt-0.5">Comprehensive VLA form compiler</p>
              </div>
            </div>
            {openSection === "aid-form" ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {openSection === "aid-form" && (
            <div className="px-6 pb-6 pt-2 border-t border-slate-100 space-y-6 text-sm text-slate-655 leading-relaxed">
              <div>
                <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> What it does</h4>
                <p>Compiles and prepares the full Victoria Legal Aid application document, including means declarations and practitioner declaration records.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Save className="w-4 h-4 text-amber-600" /> Saving Drafts</h5>
                  <p className="text-xs text-slate-500 mb-2">Manage drafts via the <strong>"Drafts & Share"</strong> dropdown menu at the top:</p>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Save Local Draft</strong>: Adds a snapshot to your browser draft history.</li>
                    <li><strong>Share Link</strong>: Encrypts form state in a shareable link.</li>
                    <li><strong>Export JSON</strong>: Downloads the draft file directly to your PC.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Download className="w-4 h-4 text-amber-600" /> Final Outputs</h5>
                  <p className="text-xs text-slate-500 mb-2">Access these via the <strong>"Print / Download"</strong> menu at the top:</p>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Print/Save PDF</strong>: Customizes layout margins and opens browser printer view.</li>
                    <li><strong>Download PDF</strong>: Compiles and downloads a clean, print-ready PDF file instantly.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Mail className="w-4 h-4 text-amber-600" /> Emailing</h5>
                  <p className="text-xs text-slate-500 mb-2">Click the prominent **"Send Email"** blue button to configure:</p>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Recipient & CC</strong>: Enter multiple target email addresses.</li>
                    <li><strong>Automatic Attachments</strong>: Automatically attaches the compiled application PDF along with any uploaded files (Centrelink cards, bank records, priors).</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3. JBL Medical Worksheet */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("medical-worksheet")}
            className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <Clipboard className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">3. JBL Medical Worksheet</h3>
                <p className="text-xs text-slate-400 mt-0.5">Psychologist, psychiatrist & medical report worksheets</p>
              </div>
            </div>
            {openSection === "medical-worksheet" ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {openSection === "medical-worksheet" && (
            <div className="px-6 pb-6 pt-2 border-t border-slate-100 space-y-6 text-sm text-slate-655 leading-relaxed">
              <div>
                <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> What it does</h4>
                <p>Documents VLA expert report funding checklists (mitigation and evidentiary criteria) to claim psychiatric, psychologist, or medical report costs.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Save className="w-4 h-4 text-amber-600" /> Saving Drafts</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Share Link</strong>: Click <em>"Share Draft Link"</em> to copy a direct URL containing all current inputs.</li>
                    <li><strong>Save to Matter</strong>: Click <em>"Save to Matter"</em> to save the worksheet directly to the client's local matter space.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Printer className="w-4 h-4 text-amber-600" /> Final Outputs</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Save/Print PDF</strong>: Triggers a clean print layout and opens your browser's Print dialog to save the worksheet as a PDF.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Send className="w-4 h-4 text-amber-600" /> Emailing</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Submit Worksheet</strong>: Sends the checklist values automatically to central outbox/inbox database.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 4. JBL Conflict Check */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("conflict-check")}
            className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">4. JBL Conflict Check</h3>
                <p className="text-xs text-slate-400 mt-0.5">Brief scanner & database conflict checker</p>
              </div>
            </div>
            {openSection === "conflict-check" ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {openSection === "conflict-check" && (
            <div className="px-6 pb-6 pt-2 border-t border-slate-100 space-y-6 text-sm text-slate-655 leading-relaxed">
              <div>
                <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> What it does</h4>
                <p>Upload briefs (charge sheets, police summaries, statements) to extract entity names automatically using AI and cross-reference them against conflict registries.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Save className="w-4 h-4 text-amber-600" /> Saving History</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Auto Scan Logging</strong>: Every brief scan is saved locally in browser storage.</li>
                    <li><strong>Scan History</strong>: Look at the bottom card on the scanner page to view, reload, or delete past checks.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Download className="w-4 h-4 text-amber-600" /> File Export</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Save to File</strong>: Downloads a JSON database file of the conflict check report results (`jbl_conflict_check_report_[timestamp].json`).</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-150">
                  <h5 className="font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Printer className="w-4 h-4 text-amber-600" /> Output & Sharing</h5>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-500">
                    <li><strong>Print Check</strong>: Opens a print dialog formatted with warning flags, match tables, and metadata for client files.</li>
                    <li><strong>Emailing</strong>: Print the check to PDF to save a local copy, then email it or drop it in the client folder.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
